/**
 * 今日の県の「決め方」だけ（ほかのファイルに依存しない）。
 * ブラウザとランキングのサーバー（functions/）の両方から使う。
 */

export type RandomSource = () => number;

/** 文字列から32bitの種を作る（FNV-1a）。 */
export function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** 種つきの乱数（mulberry32）。同じ種なら、どの端末でも同じ列になる。 */
export function seededRandom(seed: string): RandomSource {
  let state = hashString(seed) || 1;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * その日の県。47県を日付から決まる順番で1周ずつ回す（同じ県が続かない）。
 * `regionIds` は並び順が変わらないもの（都道府県コード順）を渡すこと。
 */
export function dailyRegionFor(dateKey: string, regionIds: readonly string[]): string {
  if (regionIds.length === 0) return '';
  const [y, m, d] = dateKey.split('-').map(Number);
  const day = Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
  const n = regionIds.length;
  const round = Math.floor(day / n);
  // 1周ごとに並びを混ぜ直す（毎年同じ日に同じ県、にならないように）
  const order = [...regionIds];
  const rng = seededRandom(`round:${round}`);
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order[((day % n) + n) % n];
}


/**
 * ランキングに送られた点数が「落とした数・遊んだ時間」でありえる範囲かを確かめる。
 *
 * 落とす料理は最大 Lv6（Lv1 の 32 個ぶん）。Lv1 1個ぶんの料理から得られる合体の点は、
 * 最上位まで育てても 2×(段数−1) ≒ 22 点で、連鎖の倍率は最大 1.5 倍。
 * なので 1回落とすごとの上限は 32 × 22 × 1.5 ≒ 1056 点。少し余裕を見て 1100 点にする。
 * 最上位どうしが触れたときのボーナス（2048点・1.2秒に1回まで）は、
 * 最上位を2つ作れるだけ落としたあと（128回以上）だけ、遊んだ時間の分を足す。
 * 落とす間隔は最短 0.43 秒なので、時間に対して落とした数が多すぎるものもはじく。
 *
 * 目的は「明らかに手で書き換えた点」をはじくこと。ぎりぎりの上手なプレイは通す。
 */
export const PLAUSIBILITY = {
  pointsPerDrop: 1100,
  slack: 2000,
  bonusPoints: 2048,
  bonusIntervalMs: 1200,
  dropsForTwoTops: 128,
  dropCooldownMs: 430,
  /** 最上位の1個に要る Lv1 の数 ÷ 1回で落ちる最大（32）。 */
  maxUnitsPerDrop: 32,
} as const;

export function isPlausibleDailyScore(entry: {
  score: number;
  maxLevel: number;
  drops: number;
  playMs: number;
}): boolean {
  const { score, maxLevel, drops, playMs } = entry;
  if (![score, maxLevel, drops, playMs].every((n) => Number.isFinite(n) && n >= 0)) return false;
  // 落とす間隔より速くは落とせない（端末の時間のぶれを見て 2 割の余裕）
  if (drops > 1 && playMs < (drops - 1) * PLAUSIBILITY.dropCooldownMs * 0.8) return false;
  // その段まで育てるには、少なくともこれだけ落としている必要がある
  const unitsNeeded = 2 ** Math.max(0, maxLevel - 1);
  if (drops * PLAUSIBILITY.maxUnitsPerDrop < unitsNeeded) return false;
  let limit = drops * PLAUSIBILITY.pointsPerDrop + PLAUSIBILITY.slack;
  if (drops >= PLAUSIBILITY.dropsForTwoTops) {
    limit += Math.floor(playMs / PLAUSIBILITY.bonusIntervalMs) * PLAUSIBILITY.bonusPoints;
  }
  return score <= limit;
}
